import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  AssignEntityTagRequestBody,
  EntityTagListResponse,
  RemoveEntityTagRequestBody,
  TaggableEntityType,
  TagListResponse,
} from "common";

const ENTITY_TAG_QUERY_KEY = "/api/v1/entity-tag";
const TAG_QUERY_KEY = "/api/v1/tag";

export const useActiveTags = (
  entityType: TaggableEntityType
): UseQueryResult<TagListResponse, Error> => {
  return useQuery<TagListResponse, Error>({
    queryKey: [TAG_QUERY_KEY, entityType],
    queryFn: async () => {
      // A guest is refused tags with 469. This read runs on page load, so it
      // degrades to no tags rather than opening the upgrade dialog (#988).
      const res = await axios.get(TAG_QUERY_KEY, {
        params: { entityType },
        skipUpgradeModal: true,
      });
      return res.data;
    },
    retry: false,
  });
};

export const useEntityTags = (
  entityType: TaggableEntityType,
  entityId: string
): UseQueryResult<EntityTagListResponse, Error> => {
  return useQuery<EntityTagListResponse, Error>({
    queryKey: [ENTITY_TAG_QUERY_KEY, entityType, entityId],
    queryFn: async () => {
      const res = await axios.get(
        `${ENTITY_TAG_QUERY_KEY}/${entityType}/${entityId}`,
        { skipUpgradeModal: true }
      );
      return res.data;
    },
    enabled: !!entityId,
    retry: false,
  });
};

export const useAssignEntityTag = (): UseMutationResult<
  void,
  ErrorNotification,
  AssignEntityTagRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, ErrorNotification, AssignEntityTagRequestBody>({
    mutationFn: async ({ entityType, entityId, tagId }) => {
      await axios.post(ENTITY_TAG_QUERY_KEY, {
        entityType,
        entityId,
        tagId,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ENTITY_TAG_QUERY_KEY,
          variables.entityType,
          variables.entityId,
        ],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "assign-tag-error",
        message: "Failed to Assign Tag",
        details: error?.response?.data?.message ?? "Failed to assign tag.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useRemoveEntityTag = (): UseMutationResult<
  void,
  ErrorNotification,
  RemoveEntityTagRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, ErrorNotification, RemoveEntityTagRequestBody>({
    mutationFn: async ({ entityType, entityId, tagId }) => {
      await axios.delete(ENTITY_TAG_QUERY_KEY, {
        data: {
          entityType,
          entityId,
          tagId,
        },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          ENTITY_TAG_QUERY_KEY,
          variables.entityType,
          variables.entityId,
        ],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "remove-tag-error",
        message: "Failed to Remove Tag",
        details: error?.response?.data?.message ?? "Failed to remove tag.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
